import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  template: `
    <div class="confirm-dialog">
      <!-- Icon -->
      <div class="dialog-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="11" stroke="currentColor" stroke-width="1.8"/>
          <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
        </svg>
      </div>

      <!-- Title -->
      <h2 class="dialog-title">{{ data.title }}</h2>

      <!-- Message -->
      <p class="dialog-message">{{ data.message }}</p>

      <!-- Divider -->
      <div class="dialog-divider"></div>

      <!-- Actions -->
      <div class="dialog-actions">
        <button class="action-btn cancel-btn" (click)="onCancel()">
          {{ data.cancelText || '取消' }}
        </button>
        <button class="action-btn confirm-btn" (click)="onConfirm()">
          {{ data.confirmText || '确定' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .confirm-dialog {
      padding: 24px 20px 20px;
      min-width: 300px;
      max-width: 360px;
      text-align: center;
      box-sizing: border-box;
    }

    /* ---- icon ---- */
    .dialog-icon {
      width: 48px;
      height: 48px;
      margin: 0 auto 18px;
      border-radius: 50%;
      background: rgba(92, 142, 127, 0.1);
      color: var(--app-accent, #5c8e7f);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* ---- title ---- */
    .dialog-title {
      margin: 0 0 10px;
      font-size: 17px;
      font-weight: 700;
      color: var(--app-text, #3a3026);
      letter-spacing: 0.3px;
      transition: color 0.3s ease;
    }

    /* ---- message ---- */
    .dialog-message {
      margin: 0 0 24px;
      font-size: 14px;
      color: var(--app-text-muted, #8e887e);
      line-height: 1.6;
      transition: color 0.3s ease;
    }

    /* ---- divider ---- */
    .dialog-divider {
      height: 1px;
      background: var(--app-border, #e8e0d5);
      margin: 0 0 20px;
      transition: background 0.3s ease;
    }

    /* ---- actions ---- */
    .dialog-actions {
      display: flex;
      gap: 12px;
      padding: 0 12px;
    }

    .action-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s ease;
      outline: none;
      line-height: 1;
    }

    .action-btn:active {
      transform: scale(0.96);
    }

    /* ---- cancel (left) ---- */
    .cancel-btn {
      background: transparent;
      border: 1.5px solid var(--app-border, #e8e0d5);
      color: var(--app-text-secondary, #6b6058);
    }

    .cancel-btn:hover {
      background: rgba(128, 128, 128, 0.04);
      border-color: var(--app-text-muted, #b0a89e);
    }

    /* ---- confirm (right) ---- */
    .confirm-btn {
      border: none;
      background: var(--app-accent, #5c8e7f);
      color: #fff;
      box-shadow: 0 2px 8px rgba(92, 142, 127, 0.2);
    }

    .confirm-btn:hover {
      box-shadow: 0 4px 14px rgba(92, 142, 127, 0.35);
    }
  `],
})
export class ConfirmDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ConfirmDialogComponent>);
  readonly data: ConfirmDialogData = inject(MAT_DIALOG_DATA);

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
